import React, { Component } from "react";
import { connect } from "react-redux";
import { withAlert } from "react-alert";

import Footer from "../../common/Footer.js";
import AnalystSideNav from "../../common/AnalystSideNav.js";
import AdminSideNav from "../../common/AdminSideNav.js";
import TopNav from "../../common/TopNav.js";
import UnauthorisedPage from "../Unauthorised";
import requireAuth from "../../hoc/requireAuth";
import authorizeAnalystOrAdmin from "../../hoc/authorizeAnalystOrAdmin";
import { reasonCodeBasedOnPriority } from "../../common/priorityStatus";

const FALLBACK_IMG =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120">' +
      '<rect width="100%" height="100%" fill="#eef0f3"/>' +
      '<text x="50%" y="50%" font-size="11" fill="#9aa0a6" ' +
      'text-anchor="middle" dy=".3em">no preview</text></svg>',
  );

/* ----------------------------- normalisers ----------------------------- */

// turn a list of url strings (or objects) into predictable photo objects
function urlsToPhotos(list, prefix) {
  return (list || []).map((item, i) => {
    const url =
      typeof item === "string" ? item : item.url || item.imageUrl || "";
    return {
      id: `${prefix}-${i}`,
      url,
      name: url ? url.split("/").pop().split("?")[0] : `image-${i + 1}`,
    };
  });
}

// map a raw PhotoCompareRecord document into the shape this page renders
function normalizeInfringement(doc) {
  const rawId = doc.id || doc._id;
  const id = rawId ? String(rawId) : "";

  return {
    id,
    sNo: doc.sNo,
    company: doc.company || "",
    brand: doc.brand || "",
    employeeCode: doc.employeeCode || "",
    notes: doc.notes || "",
    // the infringing listing's URL — schema field is `infringingLink`
    epageUrl: doc.infringingLink || doc.url || doc.epageUrl || "",
    // brand-owner reference URL
    comparativeLink: doc.comparativeLink || "",
    // these were the old ecommerce-derived display fields; the new schema
    // doesn't carry them, so fall back to safe defaults.
    marketplace: doc.marketplace || doc.site || "",
    sellerName: doc.sellerName || doc.username || "",
    reportedDate: doc.reportedDate || doc.createdAt || "",
    status: doc.photoCompareStatus || "pending",
    reasonCode: "",
    reasonLabel: "",
    priority: "",
    // schema fields are originalImages and editImage (singular)
    originalImages: urlsToPhotos(
      doc.originalImages || doc.originalImageUrls,
      `${id}-o`,
    ),
    editedImages: urlsToPhotos(
      doc.editImage || doc.editedImageUrls,
      `${id}-e`,
    ),
  };
}

const THUMB_PAGE_SIZE = 20; // thumbnails shown per side before "show more"

// the default per-card UI state (pending pick + the pairs being built)
const emptyCard = () => ({
  pending: null, // { side: "original"|"edited", photo }
  pairs: [], // [{ original, edited }]
  submitting: false,
  submitted: false,
  // how many thumbnails to show per side (grows when analyst clicks "show more")
  visibleOriginal: THUMB_PAGE_SIZE,
  visibleEdited: THUMB_PAGE_SIZE,
});

class PhotoComparePage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      authorised: true,

      // filters — all values come from PhotoCompareRecord distinct queries
      filtersLoading: false,
      companies: [],        // distinct company strings
      brands: [],           // distinct brand strings (scoped to selected company)
      employeeCodes: [],    // distinct employeeCode strings (scoped to company+brand)
      selectedCompany: "",
      selectedBrand: "",
      selectedEmployeeCode: "",
      // pending = fresh inbox, unsure = flagged, submitted = done, all = everything
      reviewStatus: "pending",

      // results
      infringements: [],
      loading: false,
      loaded: false,

      // pagination — driven by mongoose-paginate-v2 on the server
      page: 1,
      perPage: 20,
      totalDocs: 0,
      totalPages: 0,
      hasPrevPage: false,
      hasNextPage: false,

      // per-infringement working state, keyed by infringement id.
      // Preserved across page changes so unsaved drafts don't disappear when
      // the user clicks Next.
      cards: {},

      // full-size preview overlay: holds the photo currently being viewed
      preview: null,

      // which required filter fields failed the last validation pass
      formErrors: { company: false, brand: false, date: false },

      // excel import state
      importing: false,
    };

    // hidden file input ref for the Excel upload button
    this.fileInputRef = React.createRef();
  }

  // clear one field's error once the user has filled it in
  clearError = (field) => {
    if (!this.state.formErrors[field]) return;
    this.setState((prev) => ({
      formErrors: { ...prev.formErrors, [field]: false },
    }));
  };

  componentDidMount() {
    window.addEventListener("keydown", this.handleKeyDown);
    this.loadFilters();
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  // ESC closes the preview overlay
  handleKeyDown = (e) => {
    if (e.key === "Escape" && this.state.preview) {
      this.setState({ preview: null });
    }
  };

  openPreview = (photo, e) => {
    // stop the surrounding thumbnail button from also triggering a pair-pick
    if (e) e.stopPropagation();
    this.setState({ preview: photo });
  };

  closePreview = () => this.setState({ preview: null });

  /* ------------------------------ data ------------------------------ */

  // Fetch distinct company / brand / employeeCode values from PhotoCompareRecord.
  // Pass { company, brand } to scope downstream lists.
  loadFilters = async ({ company = "", brand = "" } = {}) => {
    this.setState({ filtersLoading: true });
    try {
      const params = {};
      if (company) params.company = company;
      if (brand) params.brand = brand;
      const res = await window.axios.get("/ecommerce/photo-compare/filters", {
        params,
      });
      const data = res.data || {};
      this.setState({
        companies: data.companies || [],
        brands: data.brands || [],
        employeeCodes: data.employeeCodes || [],
        filtersLoading: false,
      });
    } catch (err) {
      console.error("Failed to load filters", err);
      this.setState({ filtersLoading: false });
    }
  };

  onCompanyChange = (company) => {
    this.setState(
      {
        selectedCompany: company,
        selectedBrand: "",
        selectedEmployeeCode: "",
        brands: [],
        employeeCodes: [],
        infringements: [],
        cards: {},
        loaded: false,
        page: 1,
        totalDocs: 0,
        totalPages: 0,
        formErrors: { company: !company, brand: false, date: false },
      },
      () => this.loadFilters({ company }),
    );
  };

  onBrandChange = (brand) => {
    const { selectedCompany } = this.state;
    this.setState({
      selectedBrand: brand,
      infringements: [],
      cards: {},
      loaded: false,
      page: 1,
      totalDocs: 0,
      totalPages: 0,
      formErrors: { company: !selectedCompany, brand: !brand, date: false },
    });
  };

  // entry point — clicking the Load button. Always starts on page 1.
  loadInfringements = async () => {
    const { selectedCompany, selectedBrand } = this.state;

    const formErrors = {
      company: !selectedCompany,
      brand: false,
      date: false,
    };
    if (formErrors.company) {
      this.setState({ formErrors });
      this.props.alert.show("Please fill all required fields");
      return;
    }

    // fresh filter → reset cards + drafts. (Page-to-page changes keep them.)
    this.setState(
      {
        formErrors: { company: false, brand: false, date: false },
        cards: {},
        page: 1,
      },
      () => this.fetchPage(),
    );
  };

  // fetch a specific page. Cards persist across pages so drafts aren't lost.
  fetchPage = async () => {
    const {
      selectedCompany,
      selectedBrand,
      selectedEmployeeCode,
      page,
      perPage,
      reviewStatus,
    } = this.state;

    this.setState({ loading: true, loaded: false, infringements: [] });

    try {
      // selectedCompany / selectedBrand are already the plain string values
      // from the PhotoCompareRecord distinct query — send them directly.
      const params = {
        company: selectedCompany,
        brand: selectedBrand,
        perPage,
        page,
        reviewStatus,
      };
      if (selectedEmployeeCode) params.employeeCode = selectedEmployeeCode;

      const res = await window.axios.get("/ecommerce/photo-compare", {
        params,
      });
      const body = res.data || {};
      const docs =
        body.docs ||
        body.items ||
        body.result ||
        (Array.isArray(body) ? body : []);

      const infringements = docs.map(normalizeInfringement);

      // merge — preserve any existing card (drafts!) for an id we've seen before
      this.setState(
        (prev) => {
          const cards = { ...prev.cards };
          infringements.forEach((inf) => {
            if (!cards[inf.id]) cards[inf.id] = emptyCard();
          });
          return {
            infringements,
            cards,
            loading: false,
            loaded: true,
            totalDocs: body.totalDocs || 0,
            totalPages: body.totalPages || 0,
            hasPrevPage: !!body.hasPrevPage,
            hasNextPage: !!body.hasNextPage,
            page: body.page || prev.page,
          };
        },
        () => {
          // keep the loaded page in view
          window.scrollTo({ top: 0, behavior: "smooth" });
        },
      );

      // pull already-saved pairs for these documents and seed the cards
      if (infringements.length > 0) {
        await this.loadExistingPairs(infringements);
      }
    } catch (err) {
      console.error("Failed to load infringements", err);
      this.props.alert.show("Could not load infringements");
      this.setState({ loading: false, loaded: true });
    }
  };

  // pagination handlers
  goToPage = (nextPage) => {
    const { totalPages, loading } = this.state;
    if (loading) return;
    if (nextPage < 1) return;
    if (totalPages && nextPage > totalPages) return;
    this.setState({ page: nextPage }, () => this.fetchPage());
  };

  changePerPage = (e) => {
    const perPage = parseInt(e.target.value, 10) || 20;
    this.setState({ perPage, page: 1 }, () => this.fetchPage());
  };

  // changing the review-status filter resets to page 1 and refetches
  changeReviewStatus = (e) => {
    const reviewStatus = e.target.value;
    this.setState({ reviewStatus, page: 1 }, () => {
      // only refetch if a search has already been run
      if (this.state.loaded) this.fetchPage();
    });
  };

  // drop a card from the current view (after submit / mark-confused) so the
  // analyst doesn't have to wait for a refetch to see it disappear.
  removeCardFromView = (infId) => {
    this.setState((prev) => ({
      infringements: prev.infringements.filter((i) => i.id !== infId),
      totalDocs: Math.max(0, prev.totalDocs - 1),
    }));
  };

  markAsUnsure = async (inf) => {
    const ok = window.confirm(
      'Mark this record as "Unsure"? It will be hidden from the default list.',
    );
    if (!ok) return;
    try {
      await window.axios.post(`/ecommerce/${inf.id}/photo-compare-review`, {
        status: "unsure",
      });
      this.props.alert.show("Marked as unsure");
      this.removeCardFromView(inf.id);
    } catch (err) {
      console.error("Failed to mark as unsure", err);
      this.props.alert.show("Could not mark as unsure");
    }
  };

  // Excel import handlers
  openFilePicker = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = ""; // allow re-selecting the same file
      this.fileInputRef.current.click();
    }
  };

  onImportFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    this.setState({ importing: true });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await window.axios.post(
        "/ecommerce/photo-compare/import",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const { inserted = 0, skipped = 0, errors = [] } = res.data || {};

      const errCount = Array.isArray(errors) ? errors.length : 0;
      this.props.alert.show(
        `Imported ${inserted} record(s)` +
          (skipped ? ` · ${skipped} skipped (duplicates)` : "") +
          (errCount ? ` · ${errCount} row error(s)` : ""),
      );

      // refresh the list if a search has already been run
      if (this.state.loaded) this.fetchPage();
    } catch (err) {
      console.error("Excel import failed", err);
      const msg =
        (err && err.response && err.response.data && err.response.data.message) ||
        "Import failed";
      this.props.alert.show(msg);
    } finally {
      this.setState({ importing: false });
    }
  };

  // bulk-fetch already-persisted pairs and merge them into the in-memory cards
  loadExistingPairs = async (infringements) => {
    const list = infringements || this.state.infringements;
    if (!list || list.length === 0) return;
    const ids = list
      .map((inf) => inf.id)
      .filter(Boolean)
      .join(",");

    if (!ids) {
      console.warn("[PhotoCompare] no ecommerce ids to look up pairs for");
      return;
    }

    try {
      const res = await window.axios.get("/ecommerce/photo-pairs", {
        params: { recordIds: ids },
      });
      const byId =
        (res.data && (res.data.pairsByRecordId || res.data.pairsByEcommerceId)) ||
        {};
      console.log("[PhotoCompare] existing pairs response", {
        requestedIds: ids.split(","),
        responseKeys: Object.keys(byId),
        totalPairs: res.data && res.data.count,
      });

      // turn server pairs into the same {original, edited} shape the card uses,
      // then merge with any in-memory drafts so paging back to a card doesn't
      // wipe unsaved work.
      this.setState((prev) => {
        const cards = { ...prev.cards };
        list.forEach((inf) => {
          const serverPairs =
            byId[inf.id] || byId[String(inf.id)] || byId[inf._id] || [];
          const fromServer = serverPairs.map((sp) => ({
            serverPairId: sp.id || sp._id,
            original: {
              id: `${inf.id}-server-o-${sp._id || sp.id}`,
              url: sp.originalImageUrl,
              name: (sp.originalImageUrl || "").split("/").pop().split("?")[0],
            },
            edited: {
              id: `${inf.id}-server-e-${sp._id || sp.id}`,
              url: sp.editedImageUrl,
              name: (sp.editedImageUrl || "").split("/").pop().split("?")[0],
            },
          }));

          // keep local drafts (no serverPairId), drop any local copies whose
          // pair now exists on the server (avoids dupes after a save→reload)
          const serverKeys = new Set(
            fromServer.map((p) => `${p.original.url}||${p.edited.url}`),
          );
          const existingDrafts = ((cards[inf.id] && cards[inf.id].pairs) || [])
            .filter((p) => !p.serverPairId)
            .filter(
              (p) => !serverKeys.has(`${p.original.url}||${p.edited.url}`),
            );

          cards[inf.id] = {
            ...(cards[inf.id] || emptyCard()),
            pairs: [...fromServer, ...existingDrafts],
            submitted: fromServer.length > 0,
          };
        });
        return { cards };
      });
    } catch (err) {
      console.error("Failed to load existing pairs", err);
      // non-fatal — analyst can still build new pairs
    }
  };

  reloadPairsFor = async (infId) => {
    const inf = this.state.infringements.find((i) => i.id === infId);
    if (inf) await this.loadExistingPairs([inf]);
  };

  /* --------------------------- card helpers --------------------------- */

  patchCard(infId, patch) {
    this.setState((prev) => ({
      cards: {
        ...prev.cards,
        [infId]: { ...(prev.cards[infId] || emptyCard()), ...patch },
      },
    }));
  }

  // click on a thumbnail: builds a pair as soon as one image from each side is picked
  handleThumbClick = (inf, side, photo) => {
    const card = this.state.cards[inf.id] || emptyCard();
    const pending = card.pending;

    if (pending && pending.side === side && pending.photo.id === photo.id) {
      // clicking the pending image again clears it
      this.patchCard(inf.id, { pending: null });
      return;
    }

    if (pending && pending.side !== side) {
      // we already had the other side pending -> form the pair
      const original = side === "original" ? photo : pending.photo;
      const edited = side === "edited" ? photo : pending.photo;
      this.addPair(inf.id, original, edited);
      return;
    }

    // otherwise just mark this image as the pending pick
    this.patchCard(inf.id, { pending: { side, photo } });
  };

  addPair(infId, original, edited) {
    const card = this.state.cards[infId] || emptyCard();
    // dedup by URL — server-loaded pairs use synthetic ids, so id-only check misses them
    const duplicate = card.pairs.some(
      (p) => p.original.url === original.url && p.edited.url === edited.url,
    );
    if (duplicate) {
      this.props.alert.show("That pair has already been added");
      this.patchCard(infId, { pending: null });
      return;
    }
    this.patchCard(infId, {
      pairs: [...card.pairs, { original, edited }],
      pending: null,
      submitted: false,
    });
  }

  removePair = async (infId, index) => {
    const card = this.state.cards[infId] || emptyCard();
    const pair = card.pairs[index];
    if (!pair) return;

    // persisted pair → delete it on the server first
    if (pair.serverPairId) {
      const ok = window.confirm("Delete this saved pair?");
      if (!ok) return;
      try {
        await window.axios.delete(
          `/ecommerce/${infId}/photo-pairs/${pair.serverPairId}`,
        );
      } catch (err) {
        console.error("Failed to unpair", err);
        this.props.alert.show("Could not unpair on server");
        return;
      }
    }

    this.patchCard(infId, {
      pairs: card.pairs.filter((_, i) => i !== index),
      submitted: false,
    });
  };

  submitPairs = async (inf) => {
    const card = this.state.cards[inf.id] || emptyCard();
    // only send pairs that aren't already on the server
    const newPairs = card.pairs.filter((p) => !p.serverPairId);
    if (newPairs.length === 0) {
      this.props.alert.show("Nothing new to submit");
      return;
    }

    this.patchCard(inf.id, { submitting: true });

    const payload = {
      pairs: newPairs.map((p) => ({
        originalImageUrl: p.original.url,
        editedImageUrl: p.edited.url,
      })),
    };

    try {
      const res = await window.axios.post(
        `/ecommerce/${inf.id}/photo-pairs`,
        payload,
      );
      const added = (res.data && res.data.added) || [];
      const skipped = (res.data && res.data.skipped) || [];

      let msg = `${added.length} pair(s) saved`;
      if (skipped.length) msg += ` · ${skipped.length} skipped`;
      this.props.alert.show(msg);

      this.patchCard(inf.id, { submitting: false, submitted: true });

      // backend auto-flips the record to "submitted" once at least one pair
      // is added; remove the card from the current view (which is filtered
      // to the analyst's chosen status — usually "pending").
      if (added.length > 0 && this.state.reviewStatus !== "all") {
        this.removeCardFromView(inf.id);
      }
    } catch (err) {
      console.error(err);
      this.props.alert.show("Submission failed. Please try again.");
      this.patchCard(inf.id, { submitting: false });
    }
  };

  /* ------------------------------ render ------------------------------ */

  renderThumb(inf, side, photo, card) {
    const pending = card.pending;
    const isPending =
      pending && pending.side === side && pending.photo.id === photo.id;
    // is this image already used inside one of the formed pairs?
    const usedInPair = card.pairs.some((p) =>
      side === "original"
        ? p.original.id === photo.id
        : p.edited.id === photo.id,
    );

    return (
      <button
        type="button"
        key={photo.id}
        title={photo.name}
        className={
          "photo-compare-thumb" +
          (isPending ? " selected" : "") +
          (usedInPair ? " is-used" : "")
        }
        onClick={() => this.handleThumbClick(inf, side, photo)}
      >
        <img
          src={photo.url || FALLBACK_IMG}
          alt={photo.name}
          loading="lazy"
          onError={(e) => {
            e.target.src = FALLBACK_IMG;
          }}
        />
        {isPending && (
          <span className="check-badge">
            <i className="material-icons">check</i>
          </span>
        )}
        {usedInPair && !isPending && <span className="use-badge">paired</span>}

        {/* hover-zoom button — opens the lightbox without forming a pair */}
        <span
          role="button"
          tabIndex={-1}
          className="photo-zoom-btn"
          title="Preview full size"
          onClick={(e) => this.openPreview(photo, e)}
        >
          <i className="material-icons">zoom_in</i>
        </span>
      </button>
    );
  }

  renderImageSection(inf, side, label, card) {
    const images = side === "original" ? inf.originalImages : inf.editedImages;
    const visibleKey = side === "original" ? "visibleOriginal" : "visibleEdited";
    const visible = card[visibleKey] || THUMB_PAGE_SIZE;
    const shown = images.slice(0, visible);
    const remaining = images.length - shown.length;

    return (
      <div className="mb-2">
        <div className="photo-compare-section-label">
          <span className={"dot dot-" + side} />
          {label}
          <span className="badge badge-light ml-2">{images.length}</span>
        </div>
        <div className="photo-compare-scroll sm">
          {images.length === 0 ? (
            <div className="photo-compare-empty py-3">
              <p className="mb-0">No {label.toLowerCase()}</p>
            </div>
          ) : (
            <>
              <div className="photo-compare-grid">
                {shown.map((photo) => this.renderThumb(inf, side, photo, card))}
              </div>
              {remaining > 0 && (
                <button
                  type="button"
                  className="btn btn-sm btn-link mt-1"
                  onClick={() =>
                    this.patchCard(inf.id, {
                      [visibleKey]: visible + THUMB_PAGE_SIZE,
                    })
                  }
                >
                  Show {Math.min(remaining, THUMB_PAGE_SIZE)} more
                  <span className="text-muted ml-1">({remaining} remaining)</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  renderPairBuilder(inf, card) {
    const pending = card.pending;
    return (
      <div className="photo-pair-builder">
        {pending ? (
          <div className="photo-pair-pending">
            <img
              src={pending.photo.url}
              alt=""
              onError={(e) => {
                e.target.src = FALLBACK_IMG;
              }}
            />
            <span>
              <strong>
                {pending.side === "original" ? "Original" : "Edited"}
              </strong>{" "}
              photo selected — now click the matching{" "}
              {pending.side === "original" ? "edited" : "original"} photo to
              form a pair.
            </span>
            <button
              type="button"
              className="btn btn-sm btn-link text-muted"
              onClick={() => this.patchCard(inf.id, { pending: null })}
            >
              cancel
            </button>
          </div>
        ) : (
          <div className="photo-pair-hint">
            <i className="material-icons">touch_app</i>
            Click an original photo, then its fake edited photo, to pair them.
          </div>
        )}
      </div>
    );
  }

  renderPairList(inf, card) {
    if (card.pairs.length === 0) return null;
    return (
      <div className="photo-pair-list">
        {card.pairs.map((p, i) => (
          <div className="photo-pair-item" key={i}>
            <span className="photo-pair-index">{i + 1}</span>
            <img
              src={p.original.url}
              alt=""
              title="Click to preview"
              onClick={() => this.openPreview(p.original)}
              onError={(e) => {
                e.target.src = FALLBACK_IMG;
              }}
            />
            <i className="material-icons photo-pair-arrow">arrow_forward</i>
            <img
              src={p.edited.url}
              alt=""
              title="Click to preview"
              onClick={() => this.openPreview(p.edited)}
              onError={(e) => {
                e.target.src = FALLBACK_IMG;
              }}
            />
            <span className="photo-pair-names">
              <span className="text-truncate">{p.original.name}</span>
              <span className="text-truncate text-muted">{p.edited.name}</span>
            </span>
            {p.serverPairId ? (
              <span
                className="badge badge-success ml-2"
                title="This pair is saved on the server"
              >
                Saved
              </span>
            ) : (
              <span
                className="badge badge-light ml-2"
                title="Draft — click Submit to save"
              >
                Draft
              </span>
            )}
            <button
              type="button"
              className="photo-pair-remove"
              title={p.serverPairId ? "Delete saved pair" : "Remove draft pair"}
              onClick={() => this.removePair(inf.id, i)}
            >
              <i className="material-icons">close</i>
            </button>
          </div>
        ))}
      </div>
    );
  }

  renderInfringementCard(inf) {
    const card = this.state.cards[inf.id] || emptyCard();

    return (
      <div className="card photo-compare-card mb-4" key={inf.id}>
        {/* header: chips + the two comparative links + meta strip */}
        <div className="photo-compare-cardhead">
          <div className="photo-compare-chiprow">
            {inf.sNo ? (
              <span className="chip chip-id" title="Row in the imported sheet">
                #{inf.sNo}
              </span>
            ) : null}
            {inf.company ? (
              <span className="chip chip-marketplace">{inf.company}</span>
            ) : null}
            {inf.brand ? (
              <span className="chip chip-reason">{inf.brand}</span>
            ) : null}
            {inf.employeeCode ? (
              <span className="chip" title="Employee code">
                <i className="material-icons">badge</i>
                {inf.employeeCode}
              </span>
            ) : null}
            <span
              className={"chip chip-status status-" + (inf.status || "pending")}
              title="Review status"
            >
              {inf.status || "pending"}
            </span>
          </div>

          {inf.notes ? (
            <div className="photo-compare-notes text-muted small px-3 pb-2">
              <i className="material-icons" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>
                sticky_note_2
              </i>
              {inf.notes}
            </div>
          ) : null}

          {/* two URL rows — comparative (legit) vs infringing (edited) */}
          <div className="photo-compare-links">
            <div className="link-row link-row-original">
              <span className="link-label">
                <i className="material-icons">verified</i>
                Comparative URL
              </span>
              {inf.comparativeLink ? (
                <a
                  href={inf.comparativeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-url"
                  title={inf.comparativeLink}
                >
                  {inf.comparativeLink}
                  <i className="material-icons link-open">open_in_new</i>
                </a>
              ) : (
                <span className="link-empty">— not provided —</span>
              )}
            </div>
            <div className="link-row link-row-edited">
              <span className="link-label">
                <i className="material-icons">report</i>
                Infringing URL
              </span>
              {inf.epageUrl ? (
                <a
                  href={inf.epageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-url"
                  title={inf.epageUrl}
                >
                  {inf.epageUrl}
                  <i className="material-icons link-open">open_in_new</i>
                </a>
              ) : (
                <span className="link-empty">— not provided —</span>
              )}
            </div>
          </div>
        </div>

        <div className="card-body">
          {this.renderImageSection(inf, "original", "Original Images", card)}
          {this.renderImageSection(inf, "edited", "Edited Images", card)}

          {this.renderPairBuilder(inf, card)}
          {this.renderPairList(inf, card)}

          {/* actions */}
          <div className="photo-compare-actions">
            <button
              type="button"
              className="btn btn-outline-warning btn-sm"
              disabled={card.submitting}
              onClick={() => this.markAsUnsure(inf)}
              title="Hide from inbox — come back to it later via the Unsure filter"
            >
              Mark as Unsure
            </button>

            <div className="flex-spacer" />

            {(() => {
              const savedCount = card.pairs.filter(
                (p) => p.serverPairId,
              ).length;
              const draftCount = card.pairs.length - savedCount;
              return (
                <>
                  <span className="photo-compare-count">
                    {savedCount} saved · {draftCount} draft
                  </span>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={draftCount === 0 || card.submitting}
                    onClick={() => this.submitPairs(inf)}
                  >
                    {card.submitting
                      ? "Submitting…"
                      : draftCount === 0
                        ? "Nothing to save"
                        : `Save ${draftCount} new pair(s)`}
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    );
  }

  renderPreview() {
    const photo = this.state.preview;
    if (!photo) return null;
    return (
      <div
        className="photo-preview-overlay"
        role="dialog"
        aria-modal="true"
        onClick={this.closePreview}
      >
        <button
          type="button"
          className="photo-preview-close"
          title="Close (Esc)"
          onClick={this.closePreview}
        >
          <i className="material-icons">close</i>
        </button>
        {/* stopPropagation on the image so clicks on it don't close the overlay */}
        <img
          className="photo-preview-img"
          src={photo.url || FALLBACK_IMG}
          alt={photo.name}
          onClick={(e) => e.stopPropagation()}
          onError={(e) => {
            e.target.src = FALLBACK_IMG;
          }}
        />
        <div className="photo-preview-caption">{photo.name}</div>
      </div>
    );
  }

  renderResults() {
    const { loading, loaded, infringements } = this.state;

    if (loading) {
      return (
        <div className="card card-small mb-4">
          <div className="card-body text-center text-muted py-5">
            <div className="spinner-border text-primary mb-2" role="status" />
            <p className="mb-0">Loading infringements…</p>
          </div>
        </div>
      );
    }

    if (!loaded) {
      return (
        <div className="card card-small mb-4">
          <div className="card-body text-center text-muted py-5">
            <i className="material-icons" style={{ fontSize: "44px" }}>
              filter_alt
            </i>
            <p className="mb-0">
              Select a brand and reporting date, then load the infringements.
            </p>
          </div>
        </div>
      );
    }

    if (infringements.length === 0) {
      return (
        <div className="card card-small mb-4">
          <div className="card-body text-center text-muted py-5">
            <i className="material-icons" style={{ fontSize: "44px" }}>
              inbox
            </i>
            <p className="mb-0">No infringements found for this selection.</p>
          </div>
        </div>
      );
    }

    return (
      <>
        {this.renderPagination("top")}
        {infringements.map((inf) => this.renderInfringementCard(inf))}
        {this.renderPagination("bottom")}
      </>
    );
  }

  renderPagination(position) {
    const {
      page,
      perPage,
      totalDocs,
      totalPages,
      hasPrevPage,
      hasNextPage,
      infringements,
      loading,
    } = this.state;

    if (totalDocs === 0) return null;

    const from = (page - 1) * perPage + 1;
    const to = from + infringements.length - 1;

    return (
      <div
        className={
          "photo-compare-pagination d-flex flex-wrap align-items-center mb-3" +
          (position === "bottom" ? " mt-3" : "")
        }
        style={{ gap: "0.5rem" }}
      >
        <span className="text-muted small">
          Showing{" "}
          <strong>
            {from}–{to}
          </strong>{" "}
          of <strong>{totalDocs}</strong> infringing e-page(s)
        </span>

        <div className="flex-spacer" style={{ flex: 1 }} />

        {position === "top" && (
          <>
            <label className="text-muted small mb-0 mr-1">Per page</label>
            <select
              className="form-control form-control-sm"
              style={{ width: "auto", display: "inline-block" }}
              value={perPage}
              disabled={loading}
              onChange={this.changePerPage}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </>
        )}

        <div className="btn-group btn-group-sm ml-2" role="group">
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={!hasPrevPage || loading}
            onClick={() => this.goToPage(1)}
            title="First page"
          >
            «
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={!hasPrevPage || loading}
            onClick={() => this.goToPage(page - 1)}
          >
            Prev
          </button>
          <span
            className="btn btn-light disabled"
            style={{ cursor: "default" }}
          >
            Page {page} of {totalPages || 1}
          </span>
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={!hasNextPage || loading}
            onClick={() => this.goToPage(page + 1)}
          >
            Next
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={!hasNextPage || loading}
            onClick={() => this.goToPage(totalPages)}
            title="Last page"
          >
            »
          </button>
        </div>
      </div>
    );
  }

  render() {
    if (!this.state.authorised) return <UnauthorisedPage />;

    const isAdmin = this.props?.login?.user?.role === "Admin";
    const {
      filtersLoading,
      companies,
      brands,
      employeeCodes,
      selectedCompany,
      selectedBrand,
      selectedEmployeeCode,
      loading,
      formErrors,
    } = this.state;
    const reqMark = <span className="text-danger ml-1">*</span>;

    return (
      <div className="row">
        {isAdmin ? <AdminSideNav /> : <AnalystSideNav />}

        <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
          <TopNav />

          <div className="main-content-container container-fluid px-4">
            <div className="page-header row no-gutters py-4 align-items-center">
              <div className="col">
                <h3 className="page-title">Photo Comparison 🔍</h3>
                <span className="text-uppercase page-subtitle">
                  E-commerce infringement review — pair each original photo with
                  its fake edited photo
                </span>
              </div>
              <div className="col-auto">
                <input
                  ref={this.fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  style={{ display: "none" }}
                  onChange={this.onImportFile}
                />
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={this.state.importing}
                  onClick={this.openFilePicker}
                  title="Upload an Excel sheet to populate the photo-compare collection"
                >
                  <i className="material-icons" style={{ verticalAlign: "middle", marginRight: 4 }}>
                    upload_file
                  </i>
                  {this.state.importing ? "Uploading…" : "Upload Excel"}
                </button>
              </div>
            </div>

            {/* filter bar */}
            <div className="card card-small mb-4">
              <div className="card-body">
                <div className="form-row align-items-end">

                  {/* Company — distinct values from PhotoCompareRecord */}
                  <div className="col-md-3 mb-2">
                    <label className="text-muted small mb-1">
                      Company{reqMark}
                    </label>
                    <select
                      className={
                        "form-control" +
                        (formErrors.company ? " is-invalid" : "")
                      }
                      value={selectedCompany}
                      disabled={filtersLoading}
                      onChange={(e) => this.onCompanyChange(e.target.value)}
                    >
                      <option value="">
                        {filtersLoading ? "Loading…" : "Select Company"}
                      </option>
                      {companies.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    {formErrors.company && (
                      <div className="invalid-feedback d-block">
                        Company is required
                      </div>
                    )}
                  </div>

                  {/* Brand — scoped to selected company */}
                  <div className="col-md-2 mb-2">
                    <label className="text-muted small mb-1">Brand</label>
                    <select
                      className="form-control"
                      value={selectedBrand}
                      disabled={!selectedCompany || filtersLoading}
                      onChange={(e) => this.onBrandChange(e.target.value)}
                    >
                      <option value="">
                        {!selectedCompany ? "Select company first" : "All Brands"}
                      </option>
                      {brands.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>

                  {/* Employee Code — scoped to company + brand */}
                  <div className="col-md-2 mb-2">
                    <label className="text-muted small mb-1">Employee Code</label>
                    <select
                      className="form-control"
                      value={selectedEmployeeCode}
                      disabled={!selectedCompany || filtersLoading}
                      onChange={(e) =>
                        this.setState({ selectedEmployeeCode: e.target.value })
                      }
                    >
                      <option value="">All Employees</option>
                      {employeeCodes.map((ec) => (
                        <option key={ec} value={ec}>{ec}</option>
                      ))}
                    </select>
                  </div>

                  {/* Review status */}
                  <div className="col-md-2 mb-2">
                    <label className="text-muted small mb-1">Status</label>
                    <select
                      className="form-control"
                      value={this.state.reviewStatus}
                      disabled={loading}
                      onChange={this.changeReviewStatus}
                    >
                      <option value="pending">Pending</option>
                      <option value="unsure">Unsure</option>
                      <option value="submitted">Submitted</option>
                      <option value="all">All</option>
                    </select>
                  </div>

                  {/* Load button */}
                  <div className="col-md-3 mb-2">
                    <button
                      className="btn btn-primary btn-block"
                      disabled={loading || !selectedCompany}
                      onClick={this.loadInfringements}
                    >
                      {loading ? "Loading…" : "Load Records"}
                    </button>
                  </div>

                </div>
              </div>
            </div>

            {this.renderResults()}
          </div>

          <Footer />
        </main>

        {this.renderPreview()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  login: state.login,
  response: state.response,
});

export default withAlert()(
  connect(mapStateToProps)(
    authorizeAnalystOrAdmin(requireAuth(PhotoComparePage)),
  ),
);
